import { useState, useEffect, useMemo } from 'react';
import { API, formatWeight, displayDate } from '@/lib/api';
import { useSubmitGuard } from '@/lib/useSubmitGuard';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Eye, CheckCircle, Pencil, Trash2, Search } from 'lucide-react';

const billStatusColors = {
  pending: 'bg-amber-950/40 text-amber-400 border-amber-800',
  billed: 'bg-blue-950/40 text-blue-400 border-blue-800',
  approved: 'bg-emerald-950/40 text-emerald-400 border-emerald-800',
};

const getBillStatus = (bill) => bill.bill_status || (bill.is_billed ? 'billed' : 'pending');

function extractDate(isoString) {
  if (!isoString) return 'Unknown';
  return isoString.slice(0, 10);
}

function extractDateDisplay(isoString) {
  if (!isoString) return 'Unknown';
  const d = isoString.slice(0, 10);
  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return d;
}

function formatTime(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });
}

function groupByDate(bills) {
  const groups = {};
  for (const bill of bills) {
    const date = bill.bill_date || extractDate(bill.created_at);
    if (!groups[date]) groups[date] = [];
    groups[date].push(bill);
  }
  const sorted = Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
  return sorted;
}

function BillCard({ bill, sno, canSeeTotals, canEnterJwelly, canApproveBill, canEditDelete, onView, onApprove, onEdit, onDelete }) {
  const status = getBillStatus(bill);
  const totalWeight = (bill.entries || []).reduce((s, e) => s + (e.gold_weight_g || 0), 0);
  const itemCount = (bill.entries || []).length;

  return (
    <Card data-testid={`bill-card-${bill.id}`} className="border-border/60 hover:border-border transition-colors">
      <CardContent className="p-4">
        {/* Row 1: S.NO + Name + Status + Time */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-lg font-bold text-primary font-mono">{sno}</span>
              <span className="font-semibold text-sm truncate">{bill.customer_name}</span>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wide ${billStatusColors[status] || ''}`}>{status}</span>
              {bill.date_edited && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-950/40 text-amber-400 border border-amber-800">Date Edited</span>}
              <span className="text-xs text-muted-foreground">{formatTime(bill.created_at)}</span>
            </div>
            <div className="flex items-center gap-1 mt-0.5">
              <span className="text-[10px] text-muted-foreground tracking-wide">S.NO</span>
              <span className="text-xs font-mono text-muted-foreground ml-2">{bill.bill_number}</span>
            </div>
          </div>
        </div>

        {/* Row 2: Details left + Purity totals & date right */}
        <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
          <div className="flex items-center gap-1 text-xs text-muted-foreground flex-wrap">
            {bill.salesman_name && <><span className="text-muted-foreground/70">SP:</span><span>{bill.salesman_name}</span></>}
            {itemCount > 0 && <><span className="ml-2 text-muted-foreground/70">Items:</span><span>{itemCount}</span></>}
            {totalWeight > 0 && <><span className="ml-2 text-muted-foreground/70">Wt:</span><span className="font-mono">{totalWeight.toFixed(2)}g</span></>}
            {bill.customer_mobile && <><span className="ml-2 text-muted-foreground/70">Ph:</span><span className="font-mono">{bill.customer_mobile}</span></>}
          </div>
          {canSeeTotals && (
            <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
              <span className="text-[10px] text-muted-foreground font-mono">{displayDate(bill.bill_date || extractDate(bill.created_at))}</span>
              {['76', '84', '92'].map(p => (
                <span key={p} className={`text-[10px] px-1.5 py-0.5 rounded font-mono font-semibold purity-${p}`}>
                  {p}: {formatWeight(bill[`totals_${p}_g`] || 0)}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Row 3: JWELLY + Actions */}
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/40">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1" onClick={() => onView(bill)} data-testid={`bill-view-${bill.id}`}>
              <Eye size={13} /> View
            </Button>
            {bill.jwelly_bill_number && (
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-950/40 text-emerald-400 border border-emerald-800">
                JWELLY: {bill.jwelly_bill_number}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {canApproveBill && status !== 'approved' && (
              <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-500 hover:text-emerald-400"
                onClick={() => onApprove(bill.id)} data-testid={`bill-approve-${bill.id}`}>
                <CheckCircle size={15} />
              </Button>
            )}
            {canEditDelete && (
              <>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(bill)} data-testid={`bill-edit-${bill.id}`}>
                  <Pencil size={13} />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-red-400"
                  onClick={() => onDelete(bill.id)} data-testid={`bill-delete-${bill.id}`}>
                  <Trash2 size={13} />
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DateSeparator({ date, count, totals, showTotals }) {
  return (
    <div className="my-5" data-testid={`date-group-${date}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-border" />
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-bold tracking-wide px-3 py-1 rounded-md bg-amber-500/15 text-amber-400 border border-amber-500/30 font-mono">
            {extractDateDisplay(date)}
          </span>
          <span className="text-xs text-muted-foreground">{count} bill{count !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex-1 h-px bg-border" />
      </div>
      {showTotals && (totals['76'] > 0 || totals['84'] > 0 || totals['92'] > 0) && (
        <div className="flex items-center justify-center gap-3 mt-2">
          {['76', '84', '92'].map(p => totals[p] > 0 ? (
            <span key={p} className={`text-xs px-2 py-0.5 rounded font-mono font-semibold purity-${p}`}>
              {p}: {totals[p].toFixed(3)}g
            </span>
          ) : null)}
          <span className="text-xs font-mono font-bold" style={{ color: 'hsl(43, 96%, 56%)' }}>
            Total: {(totals['76'] + totals['84'] + totals['92']).toFixed(3)}g
          </span>
        </div>
      )}
    </div>
  );
}

export default function BillsPage() {
  const { user } = useAuth();
  const [bills, setBills] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [statusCounts, setStatusCounts] = useState({ pending: 0, billed: 0, approved: 0, all: 0 });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const { busy, run } = useSubmitGuard('Please wait — the previous action is still processing');
  const [filter, setFilter] = useState('all');
  const [selectedBill, setSelectedBill] = useState(null);
  const [jwellyNumber, setJwellyNumber] = useState('');
  const [editBill, setEditBill] = useState(null);
  const [editForm, setEditForm] = useState({});
  const isBillingExec = user?.role === 'billing_executive';
  const isStoreManager = user?.role === 'store_manager';
  const isAdmin = ['admin', 'analyst'].includes(user?.role);
  const canSeeTotals = ['admin', 'store_manager', 'stock_manager', 'billing_executive'].includes(user?.role);
  const canEnterJwelly = isBillingExec || isStoreManager || isAdmin;
  const canApproveBill = isStoreManager || isAdmin;
  const canEditDelete = isAdmin;

  const PAGE_SIZE = 50;

  const fetchBills = async (pageToLoad = 1, append = false) => {
    setLoading(true);
    try {
      const params = { page: pageToLoad, page_size: PAGE_SIZE };
      if (filter !== 'all') params.bill_status = filter;
      if (debouncedSearch) params.search = debouncedSearch;
      const { data } = await API.get('/bills', { params });
      setBills(prev => append ? [...prev, ...data.bills] : data.bills);
      setTotal(data.total);
      setHasMore(data.has_more);
      setStatusCounts(data.status_counts || {});
      setPage(pageToLoad);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { fetchBills(1, false); }, [filter, debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = () => fetchBills(page + 1, true);

  // Re-fetch everything currently loaded (keeps the user's scroll position after an action)
  const refresh = async () => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: Math.min(PAGE_SIZE * page, 500) };
      if (filter !== 'all') params.bill_status = filter;
      if (debouncedSearch) params.search = debouncedSearch;
      const { data } = await API.get('/bills', { params });
      setBills(data.bills); setTotal(data.total); setHasMore(data.has_more); setStatusCounts(data.status_counts || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSaveJwelly = async () => {
    if (!selectedBill) return;
    return run(async () => {
      try {
        await API.put(`/bills/${selectedBill.id}`, {
          jwelly_bill_number: jwellyNumber, is_billed: true,
          updated_by: user?.name || user?.role,
        });
        toast.success('JWELLY bill number saved & marked as billed');
        setSelectedBill(null); setJwellyNumber(''); refresh();
      } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    });
  };

  const handleApproveBill = async (billId) => {
    return run(async () => {
      try {
        await API.post(`/bills/${billId}/approve`, null, { params: { approved_by: user?.name || user?.role } });
        toast.success('Bill approved'); setSelectedBill(null); refresh();
      } catch (err) { toast.error(err.response?.data?.detail || 'Approve failed'); }
    });
  };

  const handleApproveAllBilled = async () => {
    const billedCount = statusCounts.billed || 0;
    if (!billedCount) { toast.info('No billed bills to approve'); return; }
    if (!window.confirm(`Approve all ${billedCount} billed bills?`)) return;
    return run(async () => {
      try {
        const res = await API.post('/bills/approve-all-billed', null, { params: { approved_by: user?.name || user?.role } });
        toast.success(res.data.message || 'All billed bills approved');
        refresh();
      } catch (err) { toast.error(err.response?.data?.detail || 'Bulk approve failed'); }
    });
  };

  const handleApproveAllPending = async () => {
    const pendingCount = statusCounts.pending || 0;
    if (!pendingCount) { toast.info('No pending bills to approve'); return; }
    if (!window.confirm(`Approve all ${pendingCount} pending bills?`)) return;
    return run(async () => {
      try {
        const res = await API.post('/bills/approve-all-pending', null, { params: { approved_by: user?.name || user?.role } });
        toast.success(res.data.message || 'All pending bills approved');
        refresh();
      } catch (err) { toast.error(err.response?.data?.detail || 'Bulk approve failed'); }
    });
  };

  const handleDeleteBill = async (billId) => {
    if (!window.confirm('Are you sure you want to delete this bill? This cannot be undone.')) return;
    return run(async () => {
      try {
        await API.delete(`/bills/${billId}`);
        toast.success('Bill deleted'); setSelectedBill(null); refresh();
      } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
    });
  };

  const openEditBill = (bill) => {
    setEditForm({
      customer_name: bill.customer_name || '', customer_mobile: bill.customer_mobile || '',
      customer_address: bill.customer_address || '', salesman_name: bill.salesman_name || '',
      jwelly_bill_number: bill.jwelly_bill_number || bill.mmi_bill_number || '',
      bill_status: getBillStatus(bill),
      bill_date: bill.bill_date || extractDate(bill.created_at),
    });
    setEditBill(bill); setSelectedBill(null);
  };

  const handleSaveEdit = async () => {
    if (!editBill) return;
    return run(async () => {
    try {
      const res = await API.put(`/bills/${editBill.id}`, {
        customer_name: editForm.customer_name, customer_mobile: editForm.customer_mobile,
        customer_address: editForm.customer_address, salesman_name: editForm.salesman_name,
        jwelly_bill_number: editForm.jwelly_bill_number, bill_status: editForm.bill_status,
        bill_date: editForm.bill_date,
        is_billed: editForm.bill_status === 'billed' || editForm.bill_status === 'approved',
        updated_by: user?.name || user?.role,
      });
      if (res.data?.affected_sessions?.length > 0) {
        toast.warning(`Bill updated. ${res.data.affected_sessions.length} stock-taking session(s) may need recalculation.`, { duration: 6000 });
      } else {
        toast.success('Bill updated');
      }
      setEditBill(null); refresh();
    } catch (err) { toast.error('Update failed'); }
    });
  };

  const openViewBill = (bill) => {
    setSelectedBill(bill);
    setJwellyNumber(bill.jwelly_bill_number || bill.mmi_bill_number || '');
  };

  // Bills arrive sorted by created_at desc from the server; group by date
  const sortedBills = useMemo(() => {
    return [...bills].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  }, [bills]);

  const dateGroups = useMemo(() => groupByDate(sortedBills), [sortedBills]);

  const billedCount = statusCounts.billed || 0;

  // Running serial number (newest first, across the full filtered set)
  let globalSno = total;

  return (
    <div data-testid="bills-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div><h1>Bills</h1><p className="hidden sm:block text-muted-foreground text-sm">View and manage sales bills</p></div>
        <div className="flex gap-2">
          {canApproveBill && filter === 'pending' && statusCounts.pending > 0 && (
            <Button variant="outline" onClick={handleApproveAllPending} disabled={busy} data-testid="bills-approve-all-pending-btn"
              className="w-full sm:w-auto text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/10">
              <CheckCircle size={16} className="mr-2" />Approve All ({statusCounts.pending})
            </Button>
          )}
          {canApproveBill && filter === 'billed' && billedCount > 0 && (
            <Button variant="outline" onClick={handleApproveAllBilled} disabled={busy} data-testid="bills-approve-all-billed-btn"
              className="w-full sm:w-auto text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/10">
              <CheckCircle size={16} className="mr-2" />Approve All Billed ({billedCount})
            </Button>
          )}
        </div>
      </div>

      {/* Filter tabs + search */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
        <div className="flex items-center gap-2 overflow-x-auto" data-testid="bills-filter-bar">
          {[
            { key: 'pending', label: 'Pending' },
            { key: 'approved', label: 'Approved' },
            { key: 'billed', label: 'Drafts' },
            { key: 'all', label: 'All' },
          ].map(f => (
            <button key={f.key} data-testid={`bills-filter-${f.key}`}
              className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg border whitespace-nowrap transition-colors ${filter === f.key ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-muted-foreground border-border hover:bg-muted'}`}
              onClick={() => setFilter(f.key)}>
              {f.label} ({statusCounts[f.key] || 0})
            </button>
          ))}
        </div>
        <div className="relative sm:ml-auto w-full sm:w-72">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search customer, bill no, mobile, JWELLY…"
            className="pl-8 h-9 text-sm" data-testid="bills-search-input" />
        </div>
      </div>

      {/* Date-grouped bill cards */}
      <div className="space-y-1" data-testid="bills-date-grouped-list">
        {dateGroups.map(([date, dateBills]) => {
          const dateTotals = { '76': 0, '84': 0, '92': 0 };
          dateBills.forEach(b => {
            dateTotals['76'] += b.totals_76_g || 0;
            dateTotals['84'] += b.totals_84_g || 0;
            dateTotals['92'] += b.totals_92_g || 0;
          });
          return (
            <div key={date}>
              <DateSeparator date={date} count={dateBills.length} totals={dateTotals} showTotals={user?.role === 'admin'} />
              <div className="space-y-3">
                {dateBills.map(bill => {
                  const sno = globalSno--;
                  return (
                    <BillCard
                      key={bill.id}
                      bill={bill}
                      sno={sno}
                      canSeeTotals={canSeeTotals}
                      canEnterJwelly={canEnterJwelly}
                      canApproveBill={canApproveBill}
                      canEditDelete={canEditDelete}
                      onView={openViewBill}
                      onApprove={handleApproveBill}
                      onEdit={openEditBill}
                      onDelete={handleDeleteBill}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
        {!loading && bills.length === 0 && <p className="text-center py-12 text-sm text-muted-foreground">No bills found</p>}
        {loading && bills.length === 0 && <p className="text-center py-12 text-sm text-muted-foreground" data-testid="bills-loading">Loading bills…</p>}
      </div>

      {bills.length > 0 && (
        <div className="flex flex-col items-center gap-2 py-6" data-testid="bills-pagination-footer">
          <span className="text-xs text-muted-foreground font-mono" data-testid="bills-shown-count">Showing {bills.length} of {total} bills</span>
          {hasMore && (
            <Button variant="outline" onClick={loadMore} disabled={loading} data-testid="bills-load-more-btn">
              {loading ? 'Loading…' : `Load more (${Math.min(PAGE_SIZE, total - bills.length)})`}
            </Button>
          )}
        </div>
      )}

      {/* View Bill Dialog */}
      <Dialog open={!!selectedBill} onOpenChange={() => setSelectedBill(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Bill {selectedBill?.bill_number}</DialogTitle></DialogHeader>
          {selectedBill && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Customer:</span><br /><b>{selectedBill.customer_name}</b></div>
                <div><span className="text-muted-foreground">Mobile:</span><br /><b className="font-mono">{selectedBill.customer_mobile}</b></div>
                <div><span className="text-muted-foreground">Address:</span><br />{selectedBill.customer_address || '-'}</div>
                <div><span className="text-muted-foreground">Salesman:</span><br />{selectedBill.salesman_name}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Status:</span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${billStatusColors[getBillStatus(selectedBill)] || ''}`}>{getBillStatus(selectedBill)}</span>
              </div>
              {canSeeTotals && (
                <div className="flex gap-2">
                  {['76', '84', '92'].map(p => (
                    <div key={p} className={`flex-1 text-center p-2 rounded-lg purity-${p}`}>
                      <span className="text-xs font-semibold">{p}</span>
                      <p className="font-mono font-semibold text-sm">{formatWeight(selectedBill[`totals_${p}_g`])}</p>
                    </div>
                  ))}
                </div>
              )}
              {selectedBill.entries && selectedBill.entries.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Entries</p>
                  <div className="space-y-1">
                    {selectedBill.entries.map((e, idx) => (
                      <div key={idx} className="flex justify-between text-sm border-b border-border/50 pb-1">
                        <span className="font-mono">{e.box_id} <span className="text-muted-foreground">({e.gold_purity})</span></span>
                        <span className="font-mono font-semibold">{e.gold_weight_g}g</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {canEnterJwelly && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">JWELLY Bill Number</label>
                  <div className="flex gap-2">
                    <Input value={jwellyNumber} onChange={e => setJwellyNumber(e.target.value)} placeholder="Enter JWELLY bill number" className="flex-1 font-mono" data-testid="jwelly-bill-input" />
                    <Button onClick={handleSaveJwelly} disabled={busy} data-testid="jwelly-save-btn">{busy ? 'Saving…' : 'Save & Mark Billed'}</Button>
                  </div>
                </div>
              )}
              {canApproveBill && getBillStatus(selectedBill) !== 'approved' && (
                <Button className="w-full" disabled={busy} onClick={() => handleApproveBill(selectedBill.id)} data-testid="bill-approve-dialog-btn">
                  <CheckCircle size={16} className="mr-2" />{busy ? 'Approving…' : 'Approve Bill'}
                </Button>
              )}
              {canEditDelete && (
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => openEditBill(selectedBill)}><Pencil size={14} className="mr-2" />Edit Bill</Button>
                  <Button variant="destructive" className="flex-1" disabled={busy} onClick={() => handleDeleteBill(selectedBill.id)}><Trash2 size={14} className="mr-2" />Delete Bill</Button>
                </div>
              )}
              <div className="text-xs text-muted-foreground">
                Created: {new Date(selectedBill.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} | By: {selectedBill.submitted_by}
                {selectedBill.approved_by && <> | Approved by: {selectedBill.approved_by}</>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Bill Dialog */}
      <Dialog open={!!editBill} onOpenChange={() => setEditBill(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Bill {editBill?.bill_number}</DialogTitle></DialogHeader>
          {editBill && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium mb-1 block">Customer Name</label>
                  <Input value={editForm.customer_name} onChange={e => setEditForm({ ...editForm, customer_name: e.target.value })} /></div>
                <div><label className="text-sm font-medium mb-1 block">Mobile</label>
                  <Input value={editForm.customer_mobile} onChange={e => setEditForm({ ...editForm, customer_mobile: e.target.value })} className="font-mono" /></div>
              </div>
              <div><label className="text-sm font-medium mb-1 block">Address</label>
                <Input value={editForm.customer_address} onChange={e => setEditForm({ ...editForm, customer_address: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium mb-1 block">Salesman</label>
                  <Input value={editForm.salesman_name} onChange={e => setEditForm({ ...editForm, salesman_name: e.target.value })} /></div>
                <div><label className="text-sm font-medium mb-1 block">JWELLY #</label>
                  <Input value={editForm.jwelly_bill_number} onChange={e => setEditForm({ ...editForm, jwelly_bill_number: e.target.value })} className="font-mono" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium mb-1 block">Bill Date</label>
                  <Input type="date" value={editForm.bill_date} onChange={e => setEditForm({ ...editForm, bill_date: e.target.value })} className="font-mono" data-testid="bill-edit-date" /></div>
                <div><label className="text-sm font-medium mb-1 block">Status</label>
                <Select value={editForm.bill_status} onValueChange={v => setEditForm({ ...editForm, bill_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="billed">Billed</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                  </SelectContent>
                </Select>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setEditBill(null)}>Cancel</Button>
                <Button className="flex-1" onClick={handleSaveEdit} disabled={busy} data-testid="bill-edit-save-btn">{busy ? 'Saving…' : 'Save Changes'}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
